/**
 * FieldSuggestionChip
 * --------------------
 * Compact "Sirius AI" suggestion control for non-textarea template inputs.
 *
 * Behaviour mirrors `FieldStateSandwich` (which is used only by `TextAreaFieldInput`)
 * but exposes the suggestion through a small popover next to the input rather
 * than a full tab-strip:
 *   - Shows an `AutoAwesomeIcon` button next to the input
 *   - When clicked, opens a popover with the AI-suggested value, citations,
 *     audit stamp, an "Apply" button (writes the value to the user's draft),
 *     and a "Get / Refresh Sirius AI Suggestion" trigger.
 *
 * Visibility rules match `FieldStateSandwich`:
 *   - Only rendered for `currentUser.flags.isSubscriptionUser`
 *   - Hidden when `metadata.value.hideMultiStateView` is true
 */
import * as types from "_graphql-types-frontend/graphql";
import { capitalize } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";

import { useCurrentUser } from "../../../utils/hooks";
import { useFieldValue, useTemplateStoreFromContext } from "../Context/hooks";
import { FieldStamp } from "./FieldAuditStamp";
import CitationDisplay from "./CitationDisplay";

interface FieldSuggestionChipProps {
  /** Full template key, e.g. "investment.fundName" */
  templateKey: string;
  /** Suggested-value formatter. Falls back to `String(value)` if not provided. */
  formatValue?: (value: unknown) => React.ReactNode;
  /**
   * Apply the suggested value to the user's input. Receives the raw `field.value`
   * stored on the RangerGenerated field row.
   */
  onApply: (value: unknown) => void;
  /** Whether to suppress the chip (e.g. metadata.value.hideMultiStateView) */
  hideMultiStateView?: boolean | null;
}

const ICON_BTN_SX = {
  padding: "2px",
  marginLeft: "4px",
  color: "rgba(25, 118, 210, 0.7)",
  "&:hover": {
    backgroundColor: "rgba(25, 118, 210, 0.08)",
    color: "rgba(25, 118, 210, 1)",
  },
};

export default function FieldSuggestionChip({
  templateKey,
  formatValue,
  onApply,
  hideMultiStateView,
}: FieldSuggestionChipProps): JSX.Element | null {
  const currentUser = useCurrentUser();

  const { getRangerSuggestions } = useTemplateStoreFromContext()(
    useShallow(state => ({
      getRangerSuggestions: state.getRangerSuggestions,
    }))
  );

  const { field: rangerField } = useFieldValue(
    templateKey,
    types.FieldState.RangerGenerated
  );

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [noSuggestionFound, setNoSuggestionFound] = useState(false);

  const fieldKeyShort = useMemo(() => templateKey.split(".")[1], [templateKey]);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleFetch = useCallback(() => {
    setFetchError(null);
    setNoSuggestionFound(false);
    setFetching(true);
    getRangerSuggestions({
      keys: [fieldKeyShort],
      skipSave: true,
      ragLimits: 100,
    })
      .then(() => {
        setFetching(false);
        // After fetch completes, the watch query will repopulate `rangerField`.
        // If still empty, surface "no suggestion" message.
        if (!rangerField?.value) {
          setNoSuggestionFound(true);
        }
      })
      .catch((err: unknown) => {
        setFetching(false);
        setFetchError(
          err instanceof Error ? err.message : "Failed to fetch suggestion."
        );
      });
  }, [fieldKeyShort, getRangerSuggestions, rangerField?.value]);

  const handleApply = useCallback(() => {
    if (rangerField?.value !== undefined && rangerField?.value !== null) {
      onApply(rangerField.value);
      handleClose();
    }
  }, [rangerField, onApply, handleClose]);

  // Visibility gating - mirror FieldStateSandwich behaviour.
  if (hideMultiStateView) return null;
  if (!currentUser.flags.isSubscriptionUser) return null;

  const open = Boolean(anchorEl);
  const hasSuggestion =
    rangerField?.value !== undefined && rangerField?.value !== null;

  // The icon's outer/contained colour signals whether a suggestion is available.
  const tooltipText = hasSuggestion
    ? "View Sirius AI suggestion"
    : "Get Sirius AI suggestion";

  return (
    <>
      <Tooltip title={tooltipText} arrow>
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            ...ICON_BTN_SX,
            color: hasSuggestion
              ? "rgba(156, 39, 176, 0.85)"
              : ICON_BTN_SX.color,
          }}
          data-cy={`field-suggestion-chip__${fieldKeyShort}`}
          data-testid={`field-suggestion-chip__${fieldKeyShort}`}
          aria-label={tooltipText}
        >
          <AutoAwesomeIcon style={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              maxWidth: 360,
              minWidth: 260,
            },
          },
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoAwesomeIcon style={{ fontSize: 14, color: "#9c27b0" }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Sirius AI Suggestion
            </Typography>
          </Stack>

          {hasSuggestion ? (
            <>
              <Box
                sx={{
                  padding: 1,
                  backgroundColor: "#fafbfc",
                  borderRadius: 1,
                  border: "1px solid #e0e0e0",
                  fontSize: 13,
                }}
              >
                {formatValue
                  ? formatValue(rangerField!.value)
                  : String(rangerField!.value)}
              </Box>

              {rangerField?.citations && rangerField.citations.length > 0 && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Citations:
                  </Typography>
                  <CitationDisplay citations={rangerField.citations} />
                </Box>
              )}

              <FieldStamp field={rangerField} />

              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleApply}
                  data-cy="field-suggestion-chip__apply"
                >
                  Apply
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={
                    fetching ? (
                      <CircularProgress size={14} />
                    ) : (
                      <RefreshIcon style={{ fontSize: 16 }} />
                    )
                  }
                  disabled={fetching}
                  onClick={handleFetch}
                >
                  {fetching ? "Refreshing..." : "Refresh"}
                </Button>
              </Stack>
            </>
          ) : (
            <>
              {noSuggestionFound ? (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontStyle: "italic" }}
                >
                  No Sirius AI suggestion was found for this field.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No suggestion has been generated yet for this field.
                </Typography>
              )}

              <Button
                size="small"
                variant="contained"
                disabled={fetching}
                startIcon={
                  fetching ? (
                    <CircularProgress size={14} />
                  ) : (
                    <AutoAwesomeIcon style={{ fontSize: 16 }} />
                  )
                }
                onClick={handleFetch}
              >
                {fetching ? "Generating..." : "Get Sirius AI Suggestion"}
              </Button>
            </>
          )}

          {fetchError && (
            <Typography variant="caption" sx={{ color: "error.main" }}>
              {fetchError}
            </Typography>
          )}
        </Stack>
      </Popover>
    </>
  );
}
